import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import React, { useEffect } from "react";
import Loading from "../../../components/Loading";
import {
  DataRoomFileQuery,
  DataRoomFileUploadStatus,
  FileType,
  useFileVersionDownloadUrlQuery,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { Button } from "../../../components/tailwind/Button";
import { useSelector } from "react-redux";
import { dataRoomFileSelectors } from "../../../store/dataRoomFile/selector";
import { AppState } from "../../../store";
import { ArchiveBoxXMarkIcon } from "@heroicons/react/20/solid";
import {
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Spinner } from "../../../components/icons/Spinner";
import { ArchiveIcon } from "lucide-react";
import { SpreadsheetComponent } from "@syncfusion/ej2-react-spreadsheet";
import { useLocation } from "react-router-dom";
import DicomViewer from "../../../components/document_viewers/Dicom";
import CsvViewer from "../../../components/document_viewers/CsvViewer";
import { FileIcon } from "@/src/components/FileIcon";
import SyncfusionPdfViewer from "../../../components/document_viewers/SyncfusionPdfViewer";

export type FileViewFile = {
  id: string;
  fileType: DataRoomFileQuery["dataRoomFile"]["fileType"];
  uploadStatus: DataRoomFileQuery["dataRoomFile"]["uploadStatus"];
  deletedAt?: DataRoomFileQuery["dataRoomFile"]["deletedAt"];
  hasDownloadAccess: boolean;
  currentLiveVersion: { id: string };
};

interface FileViewProps {
  file: FileViewFile;
  pageIndex?: number;
  rectsOnPage?: number[][];
  activeCell?: string;
}

export function FileView(props: FileViewProps) {
  const selectedVersionId = useSelector((state: AppState) =>
    dataRoomFileSelectors.selectedFileVersionId(state, props.file.id),
  );

  return (
    <div className="border-y border-persian-100">
      {selectedVersionId !== props.file.currentLiveVersion.id ? (
        <div className="w-full h-30 bg-orange-100 rounded-t-md px-3 flex justify-center py-2">
          <p className="text-sm text-orange-700">
            You are viewing an old version of this document
          </p>
        </div>
      ) : null}
      {props.file.deletedAt ? (
        <div className="">
          <div className="w-full h-96 bg-red-100 rounded-md px-3 flex flex-col items-center justify-center py-2">
            <ArchiveBoxXMarkIcon className="h-6 w-6 text-red-700" />
            <p className="text-sm font-semibold mt-1 text-red-700">
              This file has been deleted
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-md p-0.5">
          <FileViewWrapper
            file={props.file}
            pageIndex={props.pageIndex}
            rectsOnPage={props.rectsOnPage}
            activeCell={props.activeCell}
          />
        </div>
      )}
    </div>
  );
}

function FileViewWrapper(props: FileViewProps) {
  if (
    props.file.uploadStatus !== DataRoomFileUploadStatus.Ready &&
    props.file.uploadStatus !== DataRoomFileUploadStatus.Uploaded
  ) {
    return (
      <div style={{ height: 800 }} className="flex items-center justify-center">
        <Spinner color="gray" size="s" />
        <p className="font-semibold text-gray-700 text-sm">Preparing file...</p>
      </div>
    );
  }

  if (
    props.file.fileType === FileType.Pdf ||
    props.file.fileType === FileType.Docx ||
    props.file.fileType === FileType.Pptx
  ) {
    return <FileViewContent {...props} />;
  }

  if (props.file.fileType === FileType.Qbb) {
    return (
      <div className="h-[800px] flex flex-col items-center justify-center px-12">
        <FileIcon size="l" fileType={FileType.Qbb} />
        <p className="text-sm font-semibold mt-1 text-gray-700">
          This is a QuickBooks export
        </p>

        <p className="text-sm text-gray-500 text-center mt-2">
          You must download this document and view it inside the QuickBooks
          desktop application.
        </p>
      </div>
    );
  }

  if (props.file.fileType === FileType.Zip) {
    return (
      <div className="h-[800px] flex flex-col items-center justify-center px-12">
        <ArchiveIcon className="h-8 w-8 text-gray-600" />
        <p className="text-sm font-semibold mt-1 text-gray-700">
          This is a zip file
        </p>

        <p className="text-sm text-gray-500 text-center mt-2">
          {props.file.hasDownloadAccess
            ? "You can download the zip to view the contents."
            : "Ask your admin to enable download access to the zip file."}
        </p>
      </div>
    );
  }

  return <FileViewContent file={props.file} activeCell={props.activeCell} />;
}

function FileViewContent(props: FileViewProps) {
  const client = useGqlClient();
  const [url, setUrl] = React.useState<string>("");
  const [fileDownloadError, setFileDownloadError] = React.useState<string>("");
  const query = useQuery();
  const selectedVersionId = useSelector((state: AppState) =>
    dataRoomFileSelectors.selectedFileVersionId(state, props.file.id),
  );
  const fileDownloadUrl = useFileVersionDownloadUrlQuery(
    client,
    {
      id: selectedVersionId,
    },
    {
      refetchOnWindowFocus(query) {
        if (query.isStaleByTime(1000 * 60 * 5)) {
          return true;
        }

        return false;
      },
      enabled: selectedVersionId !== "",
    },
  );

  useEffect(() => {
    if (
      fileDownloadUrl.data &&
      fileDownloadUrl.data.fileVersionDownloadUrl.viewUrl !== url
    ) {
      setFileDownloadError("");
      setUrl(fileDownloadUrl.data.fileVersionDownloadUrl.viewUrl);
      return;
    }

    if (fileDownloadUrl.error) {
      setFileDownloadError("Unable to load file");
      return;
    }
  }, [
    fileDownloadUrl.data,
    fileDownloadUrl.isRefetching,
    fileDownloadUrl.isPending,
    fileDownloadUrl.error,
    url,
  ]);

  if (
    props.file.uploadStatus !== DataRoomFileUploadStatus.Ready &&
    props.file.uploadStatus !== DataRoomFileUploadStatus.Uploaded
  ) {
    return (
      <div style={{ height: 800 }} className="flex items-center justify-center">
        <Spinner color="gray" size="s" />
        <p className="font-semibold text-gray-700 text-sm">Preparing file...</p>
      </div>
    );
  }

  if (fileDownloadError) {
    return (
      <div className="flex items-center justify-center">
        <p>Failed to load file</p>
        <Button
          variant="neutral"
          text="Retry"
          isLoading={fileDownloadUrl.isPending || fileDownloadUrl.isRefetching}
          loadingText="Retrying..."
          onClick={() => {
            fileDownloadUrl.refetch();
          }}
        />
      </div>
    );
  }

  if (url === "") {
    return (
      <div style={{ height: 800 }}>
        <Loading />
      </div>
    );
  }

  if (
    props.file.fileType === FileType.Pdf ||
    props.file.fileType === FileType.Docx ||
    props.file.fileType === FileType.Pptx
  ) {
    return (
      <div className="h-[calc(100vh-220px)] w-full max-w-full min-w-0 overflow-hidden">
        <SyncfusionPdfViewer
          url={url}
          pageIndex={props.pageIndex}
          rectsOnPage={props.rectsOnPage}
        />
      </div>
    );
  }

  if (
    props.file.fileType === FileType.Xlsx ||
    props.file.fileType === FileType.Xls
  ) {
    const jsonUrl =
      fileDownloadUrl.data?.fileVersionDownloadUrl.jsonUrl ?? "";

    if (
      !fileDownloadUrl.data &&
      (fileDownloadUrl.isPending || fileDownloadUrl.isLoading)
    ) {
      return (
        <div className="flex h-[calc(100vh-220px)] w-full max-w-full min-w-0 items-center justify-center overflow-hidden">
          <Loading />
        </div>
      );
    }

    if (jsonUrl === "") {
      return (
        <div className="flex h-[calc(100vh-220px)] w-full max-w-full min-w-0 flex-col items-center justify-center overflow-hidden px-8 text-center">
          <ComputerDesktopIcon
            className="mb-4 h-14 w-14 shrink-0 text-gray-300"
            aria-hidden
          />
          <p className="max-w-md text-sm font-medium text-gray-700">
            Viewing this document in the browser isn&apos;t supported on our
            platform.
          </p>
        </div>
      );
    }

    return (
      <div className="h-[calc(100vh-220px)] w-full max-w-full min-w-0 overflow-hidden">
        <SpreadSheet
          url={jsonUrl}
          activeCell={props.activeCell}
          sheetIndex={query.get("sheetIndex")}
          cell={query.get("cell")}
        />
      </div>
    );
  }

  if (props.file.fileType === FileType.Csv) {
    return (
      <div className="h-[calc(100vh-220px)] max-w-full overflow-hidden">
        <CsvViewer url={url} />
      </div>
    );
  }

  if (props.file.fileType === FileType.Dicom) {
    return (
      <div className="w-full">
        <DicomViewer
          key={props.file.id}
          url={url}
          width="100%"
          showTools={true}
          onError={(error) => {
            console.error("DICOM viewer error:", error);
          }}
          onImageLoad={(info) => {
            console.log("DICOM image loaded:", info);
          }}
        />
      </div>
    );
  }

  return (
    <DocViewer
      key={url}
      config={{
        header: {
          disableFileName: true,
          disableHeader: true,
        },
      }}
      prefetchMethod="GET"
      style={{ height: 650, width: "100%", overflowY: "scroll" }}
      documents={[
        {
          uri: url,
        },
      ]}
      pluginRenderers={DocViewerRenderers}
    />
  );
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export type SpreadsheetHighlightConfig = {
  columns: Array<{ column: string; color: string }>;
  startRow: number;
};

export function SpreadSheet(props: {
  url: string;
  activeCell?: string;
  sheetIndex?: string | null;
  cell?: string | null;
  highlightConfig?: SpreadsheetHighlightConfig;
  height?: string;
}) {
  const spreadsheetRef = React.useRef<SpreadsheetComponent>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [retryKey, setRetryKey] = React.useState(0);
  const [isFileLoaded, setIsFileLoaded] = React.useState(false);
  const [hasNavigated, setHasNavigated] = React.useState(false);
  const prevHighlightRef = React.useRef<string[]>([]);
  const { sheetIndex, cell, highlightConfig } = props;

  React.useEffect(() => {
    // Reset navigation state when URL changes
    setIsFileLoaded(false);
    setHasNavigated(false);
    setLoadError(null);
    setLoading(true);

    let isCancelled = false;

    const fetchData = async () => {
      try {
        const response = await fetch(props.url);
        if (isCancelled) return;

        if (!response.ok) {
          if (!isCancelled) {
            setLoadError(
              "We couldn’t load this spreadsheet. Try again in a moment.",
            );
            setLoading(false);
          }
          return;
        }

        let workbookJson: unknown;
        try {
          workbookJson = await response.json();
        } catch {
          if (!isCancelled) {
            setLoadError(
              "This file doesn’t look like a valid workbook. Try downloading it instead.",
            );
            setLoading(false);
          }
          return;
        }
        if (isCancelled) return;

        const spreadsheet = spreadsheetRef.current;

        if (!spreadsheet || isCancelled) {
          if (!isCancelled) {
            setLoadError(
              "The spreadsheet viewer failed to start. Try again.",
            );
            setLoading(false);
          }
          return;
        }

        spreadsheet.cssClass = "e-custom";
        spreadsheet.openFailure = () => {
          if (!isCancelled) {
            setLoading(false);
            setLoadError(
              "This workbook couldn’t be opened. It may be corrupted or unsupported.",
            );
          }
        };

        try {
          spreadsheet.openFromJson(
            { file: workbookJson as object, triggerEvent: true },
          );
        } catch (error) {
          if (!isCancelled) {
            console.error("Error opening spreadsheet from JSON:", error);
            setLoading(false);
            setLoadError(
              "This workbook couldn’t be opened. It may be corrupted or unsupported.",
            );
          }
          return;
        }

        if (!isCancelled) {
          setTimeout(() => {
            if (isCancelled) return;
            setLoading(false);
            setIsFileLoaded(true);
            navigateToCell();
          }, 200);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error loading spreadsheet:", error);
          setLoadError(
            "We couldn’t load this spreadsheet. Check your connection and try again.",
          );
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [props.url, retryKey]);

  const navigateToCell = React.useCallback(() => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet || !isFileLoaded) return false;

    try {
      if (sheetIndex && spreadsheet) {
        spreadsheet.activeSheetIndex = parseInt(sheetIndex);
      }

      if (cell && spreadsheet) {
        spreadsheet.goTo(cell);
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }, [isFileLoaded, sheetIndex, cell]);

  React.useEffect(() => {
    if (!isFileLoaded || hasNavigated) return;

    const navigate = () => {
      if (navigateToCell()) {
        setHasNavigated(true);
      }
    };

    // Initial navigation with delay
    setTimeout(navigate, 100);
  }, [isFileLoaded, hasNavigated, navigateToCell]);

  // Fallback to check maintain navigation if syncfusion does some weird nav glitch
  React.useEffect(() => {
    if (!isFileLoaded || !hasNavigated || (!sheetIndex && !cell)) return;

    const interval = setInterval(() => {
      const spreadsheet = spreadsheetRef.current;
      if (!spreadsheet) return;

      try {
        const currentSheet = spreadsheet.activeSheetIndex;
        const currentCell = spreadsheet.sheets[currentSheet]?.selectedRange;

        const expectedSheet = sheetIndex ? parseInt(sheetIndex) : 0;
        const needsSheetNav = sheetIndex && currentSheet !== expectedSheet;
        const needsCellNav = cell && currentCell !== cell;

        if (needsSheetNav || needsCellNav) {
          navigateToCell();
        }
      } catch (e) {
        navigateToCell();
      }
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isFileLoaded, hasNavigated, sheetIndex, cell, navigateToCell]);

  // Handle navigation when query parameters change for already loaded file
  React.useEffect(() => {
    if (!isFileLoaded) return;

    // Reset navigation flag when query parameters change
    setHasNavigated(false);
  }, [sheetIndex, cell]);

  // Serialize highlight config for dependency tracking
  const highlightConfigKey = highlightConfig
    ? JSON.stringify({
      columns: highlightConfig.columns,
      startRow: highlightConfig.startRow,
    })
    : null;

  // Apply column highlighting to all sheets
  React.useEffect(() => {
    if (!isFileLoaded || !highlightConfig) return;

    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;

    // Small delay to ensure spreadsheet is ready
    const timer = setTimeout(() => {
      try {
        const endRow = 1000; // Highlight up to row 1000
        const sheetCount = spreadsheet.sheets?.length ?? 1;
        const originalSheetIndex = spreadsheet.activeSheetIndex;

        // Apply highlighting to each sheet
        for (let sheetIdx = 0; sheetIdx < sheetCount; sheetIdx++) {
          // Switch to this sheet
          spreadsheet.activeSheetIndex = sheetIdx;

          // Clear previous column highlights on this sheet
          prevHighlightRef.current.forEach((column) => {
            const range = `${column}1:${column}${endRow}`;
            spreadsheet.cellFormat({ backgroundColor: "#FFFFFF" }, range);
          });

          // Apply new column highlights on this sheet
          highlightConfig.columns.forEach(({ column, color }) => {
            const range = `${column}${highlightConfig.startRow}:${column}${endRow}`;
            spreadsheet.cellFormat({ backgroundColor: color }, range);
          });
        }

        // Restore original sheet
        spreadsheet.activeSheetIndex = originalSheetIndex;

        // Update ref with current highlighted columns
        prevHighlightRef.current = highlightConfig.columns.map((c) => c.column);
      } catch (e) {
        console.error("Error applying highlights:", e);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [isFileLoaded, highlightConfigKey]);

  return (
    <div className="relative h-full w-full max-w-full min-w-0 overflow-hidden">
      <div
        className={`absolute inset-0 overflow-auto ${loading || loadError ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <SpreadsheetComponent
          key={`${props.url}-${retryKey}`}
          height={props.height ?? "calc(100vh - 225px)"}
          width="100%"
          ref={spreadsheetRef}
          openSettings={{
            chunkSize: 1_000_000,
            retryCount: 3,
            retryAfterDelay: 500,
          }}
          scrollSettings={{ enableVirtualization: true }}
          calculationMode="Manual"
          beforeSave={(args) => {
            args.isFullPost = false;
          }}
          showFormulaBar={false}
        />
      </div>
      {loading && !loadError ? (
        <div className="absolute inset-0 z-[1] flex items-center justify-center bg-white">
          <Loading />
        </div>
      ) : null}
      {loadError ? (
        <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center bg-white px-8 text-center">
          <ExclamationTriangleIcon
            className="mb-4 h-12 w-12 shrink-0 text-amber-500"
            aria-hidden
          />
          <p className="max-w-md text-sm font-medium text-gray-700">
            {loadError}
          </p>
          <div className="mt-5">
            <Button
              variant="neutral"
              text="Try again"
              onClick={() => setRetryKey((k) => k + 1)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
